import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  doAUTH_SESSION_UNVERIFIED_CLEAR,
  doAUTH_SESSION_UNVERIFIED_SET,
  doREQUEST_IDENTITY_SESSION_ACTIVITY,
  doREQUEST_IDENTITY_SESSION_REVALIDATE,
  doSESSION_END_REASON_SET,
  doSESSION_WARNING_ENTERED,
  SessionEndReason,
  SessionRevalidateAnswer,
} from "../../store";
import { clearIdentitySession } from "../auth/clear_identity_session";
import {
  ActivityReporter,
  ActivityReportOutcome,
  createActivityReporter,
} from "../auth/activity_reporter";
import { currentSessionScope, isSessionScopeCurrent } from "../auth/session_scope";
import {
  SESSION_DEADLINES_STORAGE_KEY,
  SessionDeadlines,
  readSessionDeadlines,
  sessionDeadlineNow,
} from "../auth/session_deadlines";
import {
  IDLE_SIGN_OUT_STORAGE_KEY,
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  LAST_ACTIVITY_STORAGE_KEY,
  SESSION_CAP_SIGN_OUT_STORAGE_KEY,
  announceIdleSignOut,
  announceSessionCapSignOut,
  clearLastActivity,
  readLastActivity,
  recordLastActivity,
} from "../auth/idle_sign_out";

// User input only: a request completing or a background poll is the app talking, not the user, and
// counting one would let an unattended screen keep itself signed in. `keydown` rather than
// `keypress`, which never fires for Backspace, Delete, the arrow keys, or a key typed into an input
// method (Chinese, Japanese) while it is composing.
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

// How often input is written to the shared record. Every write wakes every other open tab, and
// against a 30-minute limit, other tabs acting on a stamp a few seconds old costs nothing.
const ACTIVITY_WRITE_INTERVAL_MS = 10000;

// How long before the session's cap its notice appears: time to save, since nothing extends the cap
export const CAP_WARNING_MS = 10 * 60 * 1000;

// How soon the server is asked again when it keeps a session it says has no time left. It truncates
// to whole seconds and the browser counts from the send time, so the last second reads as over; a
// second on, the server has ended the session or moved its end.
const REASK_MS = 1000;

interface AuthSlice {
  auth?: {
    accessToken?: string | null;
    sessionNonce?: string | null;
    sessionDeadlines?: SessionDeadlines | null;
    sessionUnverified?: boolean;
  };
}

type ServerReading = ReturnType<typeof readSessionDeadlines>;

// What a check asks about: the warning's start, the idle end, or the idle end again once the refresh
// coordinator's next attempt answers, after a check there got no answer
type CheckKind = "warning" | "idle_end" | "idle_end_retry";

// The idle end to go by. While the server ends idle sessions it is the server's alone, since work in
// another app counts there and this browser cannot see it; with none known only the cap applies.
// Otherwise it is the browser's own limit after the last input in any tab, counted from no earlier
// than the switch away from enforcement, or an earlier end the server set.
const idleEndOf = (server: ServerReading, lastActivityAt: number): number => {
  if (server?.idleEnforced) return server.idleEndsAt ?? Infinity;
  const since = Math.max(lastActivityAt, server?.fallbackSince ?? lastActivityAt);
  return Math.min(since + IDLE_TIMEOUT_MS, server?.idleEndsAt ?? Infinity);
};

/**
 * Ends the session for idleness, or at its cap. It runs only while signed in, and reports input to the
 * server (`activity_reporter.ts`), which ends idle sessions on its own clock.
 *
 * Before it shows the idle warning or ends a session for idleness it asks the server
 * (`identity_session_revalidate.ts`), unless a reply to a request sent since that fell due is already
 * known, from this tab or another. Until an answer is applied, input does not count, as while the
 * warning is up. Past the idle end, private pages are hidden (`auth.sessionUnverified`) until the server
 * answers, and while it cannot be reached nothing is ended here. When the server enforces idle, only its
 * rejection ends the session, locally; otherwise the session ends at the browser's own limit
 * (`idleEndOf`), with the shared logout. The cap and its notice keep their own clock.
 *
 * Every tab reads and writes the same last-activity record and the same server deadlines, so a
 * background tab cannot sign the user out of the one they are working in. `signOutAt` is set for the
 * last `IDLE_WARNING_MS` before an idle end, while its blocking warning is on screen; it never is when
 * the cap comes first, since nothing can extend that. `capEndsAt` is set for the last `CAP_WARNING_MS`
 * before the cap, while its notice is on screen.
 */
export const useAutoLogoutTracker = () => {
  // a primitive, so a token refresh (a new `auth` object each time) is not a re-render
  const signedIn = useSelector((state: AuthSlice) => Boolean(state?.auth?.accessToken));
  // a sign-in, here or adopted from another tab, is a new session: nothing asked about the last one applies
  const sessionNonce = useSelector((state: AuthSlice) => state?.auth?.sessionNonce ?? null);
  // a new reply from the server is a new deadline to act on
  const sessionDeadlines = useSelector((state: AuthSlice) => state?.auth?.sessionDeadlines ?? null);
  const dispatch = useDispatch();
  const store = useStore<AuthSlice>();

  const [signOutAt, setSignOutAt] = useState<number | null>(null);
  const [capEndsAt, setCapEndsAt] = useState<number | null>(null);
  // "Stay signed in" is waiting for the server to confirm it
  const [stayPending, setStayPending] = useState(false);

  // Refs, because the listeners outlive the render that added them. `ownActivityAtRef` is this
  // tab's own last input: what it goes by when storage is unavailable. `heldRef`: an idle action fell
  // due and no answer has brought the tracker back, so input does not count.
  const ownActivityAtRef = useRef(0);
  const lastWriteAtRef = useRef(0);
  const warningRef = useRef(false);
  const capWarningRef = useRef(false);
  const heldRef = useRef(false);
  const signingOutRef = useRef(false);
  const wasSignedInRef = useRef(false);
  const evaluateRef = useRef<(() => void) | null>(null);
  const reporterRef = useRef<ActivityReporter | null>(null);

  const noteActivity = useCallback((at: number) => {
    ownActivityAtRef.current = at;
    lastWriteAtRef.current = at;
    recordLastActivity({ at });
  }, []);

  // `reason` is null when the user chose to sign out, which needs no explaining; the only session this
  // tracker ends as `SessionEnded` is one that reached its cap
  const endSession = useCallback(
    async (reason: SessionEndReason | null) => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      warningRef.current = false;
      capWarningRef.current = false;
      setSignOutAt(null);
      setCapEndsAt(null);

      // every other tab shares the clock and the cap, so it ends with this one; they skip the
      // logout request
      if (reason === SessionEndReason.SessionEnded) announceSessionCapSignOut();
      else announceIdleSignOut();
      await clearIdentitySession();
      // after the sign-out, which wipes the session record this persists into
      if (reason) dispatch(doSESSION_END_REASON_SET({ reason, at: Date.now() }));
      // a full navigation, like the navbar's sign-out, so no in-memory slice outlives the session
      window.location.href = "/";
    },
    [dispatch],
  );

  useEffect(() => {
    if (!signedIn) {
      // The session ended some other way: it expired, or another tab or the boot ended it. Left
      // behind, the record would make the next boot sign out again and blame inactivity.
      if (wasSignedInRef.current) clearLastActivity();
      wasSignedInRef.current = false;
      return;
    }
    wasSignedInRef.current = true;
    signingOutRef.current = false;
    heldRef.current = false;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let trailingWrite: ReturnType<typeof setTimeout> | null = null;
    // The one check out with the server. `live` goes false when this run ends or the check is called
    // off; one that is not used stays here, so nothing more is asked for a session that has moved on.
    let pending: { live: boolean } | null = null;
    // when the last check whose answer was used was asked: that answer came from a later request
    let answeredAsOf = -Infinity;
    let lastAskedAt = -Infinity;
    // the check at the warning's start got no answer, so the warning goes up on what is known
    let warnOnKnown = false;
    // when the next idle action falls due, so input after a timer that ran late is weighed first
    let nextIdleAt = -Infinity;

    const reporter = createActivityReporter(
      () =>
        new Promise<ActivityReportOutcome>((resolve) =>
          dispatch(
            doREQUEST_IDENTITY_SESSION_ACTIVITY({ payload: {}, setRequestResult: resolve as any }),
          ),
        ),
    );
    reporterRef.current = reporter;
    const hideWarnings = () => {
      if (warningRef.current) setSignOutAt(null);
      if (capWarningRef.current) setCapEndsAt(null);
      warningRef.current = false;
      capWarningRef.current = false;
    };

    // Each warning is shown, and announced once as it appears, so whatever must be saved before the
    // session ends can go then rather than on every tick of its countdown.
    const showIdleWarning = (at: number | null) => {
      if (at !== null && !warningRef.current) dispatch(doSESSION_WARNING_ENTERED({ kind: "idle" }));
      warningRef.current = at !== null;
      setSignOutAt(at);
    };
    const showCapWarning = (at: number | null) => {
      if (at !== null && !capWarningRef.current)
        dispatch(doSESSION_WARNING_ENTERED({ kind: "cap" }));
      capWarningRef.current = at !== null;
      setCapEndsAt(at);
    };

    const deadlineNow = () => sessionDeadlineNow(store.getState().auth?.sessionDeadlines ?? null);

    const setUnverified = (unverified: boolean) => {
      if (Boolean(store.getState().auth?.sessionUnverified) === unverified) return;
      dispatch(unverified ? doAUTH_SESSION_UNVERIFIED_SET() : doAUTH_SESSION_UNVERIFIED_CLEAR());
    };

    // Back to waiting, or to the warning until `warnUntil`: input counts again, and private pages show
    const resume = (warnUntil: number | null) => {
      heldRef.current = false;
      warnOnKnown = false;
      showIdleWarning(warnUntil);
      setUnverified(false);
    };

    // An answer is used only while this tab holds the session it was asked about. One that found the
    // session ended was acted on where it ended. No answer at the idle end leaves the session unverified,
    // and the refresh coordinator's own next attempt is the next answer: asking at once would cut its
    // retry schedule short.
    const ask = (kind: CheckKind, idleEndsAt: number) => {
      const check = { live: true };
      pending = check;
      const scope = currentSessionScope();
      const askedAt = deadlineNow();
      lastAskedAt = askedAt;
      new Promise<SessionRevalidateAnswer>((resolve) =>
        dispatch(
          doREQUEST_IDENTITY_SESSION_REVALIDATE({
            payload: { idleEndsAt, waitForRetry: kind === "idle_end_retry" },
            setRequestResult: resolve as any,
          }),
        ),
      ).then(({ outcome }) => {
        if (
          !check.live ||
          outcome === "ended" ||
          signingOutRef.current ||
          !isSessionScopeCurrent(scope)
        )
          return;
        pending = null;
        if (outcome === "success") answeredAsOf = askedAt;
        else if (kind !== "warning") return ask("idle_end_retry", idleEndsAt);
        else warnOnKnown = true;
        evaluate();
      });
    };

    // The idle half of `evaluate`, while no check is out: what is due, and when to look again
    // (`Infinity` when it has no time of its own).
    const reviewIdle = (server: ServerReading, now: number, cap: number | null): number => {
      const lastActivityAt = Math.max(ownActivityAtRef.current, readLastActivity()?.at ?? 0);
      const idleEndsAt = idleEndOf(server, lastActivityAt);
      const warningAt = idleEndsAt - IDLE_WARNING_MS;
      // when both fall together it is the cap, which no answer can extend
      const capFirst = cap !== null && cap <= idleEndsAt;
      if (capFirst || now < warningAt) {
        resume(null);
        nextIdleAt = capFirst ? Infinity : warningAt;
        return nextIdleAt;
      }
      nextIdleAt = -Infinity;

      // a reply to a request sent once `dueAt` came: this tab's own check, or any reply another tab shared
      const answered = (dueAt: number) =>
        answeredAsOf >= dueAt || (server?.policyObservedAt ?? -Infinity) >= dueAt;

      if (now < idleEndsAt) {
        if (warningRef.current || warnOnKnown || answered(warningAt)) {
          resume(idleEndsAt);
          return idleEndsAt;
        }
        heldRef.current = true;
        ask("warning", idleEndsAt);
        return Infinity;
      }

      // the idle end: nothing private shows until the server has answered for it
      heldRef.current = true;
      warnOnKnown = false;
      setUnverified(true);
      if (!answered(idleEndsAt)) {
        ask("idle_end", idleEndsAt);
        return Infinity;
      }
      if (!server?.idleEnforced) {
        void endSession(SessionEndReason.Inactivity);
        return Infinity;
      }
      const askAt = lastAskedAt + REASK_MS;
      if (now < askAt) return askAt;
      ask("idle_end", idleEndsAt);
      return Infinity;
    };

    // Re-reads the shared records every time rather than trusting a deadline worked out earlier:
    // a timer in a background tab can fire minutes late, other tabs may have seen input since, and
    // the server may have moved the deadlines.
    const evaluate = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (signingOutRef.current) return;

      const { auth } = store.getState();
      const own = auth?.sessionDeadlines ?? null;
      const server = auth?.sessionNonce ? readSessionDeadlines(own, auth.sessionNonce) : null;
      const now = sessionDeadlineNow(own);

      // the cap is armed apart from any check: nothing extends it, and no answer is waited for
      const cap = server?.capEndsAt ?? null;
      if (cap !== null && now >= cap) {
        void endSession(SessionEndReason.SessionEnded);
        return;
      }
      showCapWarning(cap !== null && cap - now <= CAP_WARNING_MS ? cap : null);

      const wakeAt = cap === null ? [] : [cap - CAP_WARNING_MS, cap];
      // while a check is out, its answer is what moves the idle side on
      if (!pending) wakeAt.push(reviewIdle(server, now, cap));
      const nextAt = Math.min(...wakeAt.filter((at) => at > now));
      if (nextAt !== Infinity) timer = setTimeout(evaluate, nextAt - now);
    };
    evaluateRef.current = evaluate;

    // the input the throttle held back, so other tabs do not measure from an older stamp
    const writeTrailingActivity = () => {
      trailingWrite = null;
      if (
        signingOutRef.current ||
        heldRef.current ||
        ownActivityAtRef.current <= lastWriteAtRef.current
      )
        return;
      noteActivity(ownActivityAtRef.current);
    };

    // once an idle action is due, only the server's answer or the warning's buttons decide it, so a
    // nudged mouse cannot keep an unattended session alive
    const inputIgnored = () => warningRef.current || heldRef.current || signingOutRef.current;

    const onInput = () => {
      if (inputIgnored()) return;
      // The timer may have run late (a laptop waking in a visible tab), so input past the next idle
      // action is weighed against it first. `evaluate` reads the record, so input elsewhere still counts.
      if (deadlineNow() >= nextIdleAt) {
        evaluate();
        if (inputIgnored()) return;
      }
      const now = Date.now();
      ownActivityAtRef.current = now;
      reporter.noteInput();
      const sinceWriteMs = now - lastWriteAtRef.current;
      if (sinceWriteMs < ACTIVITY_WRITE_INTERVAL_MS) {
        trailingWrite ??= setTimeout(
          writeTrailingActivity,
          ACTIVITY_WRITE_INTERVAL_MS - sinceWriteMs,
        );
        return;
      }
      noteActivity(now);
    };

    // "Stay signed in" in another tab, and another tab's reply from the server. Input elsewhere needs
    // no event: the timer reads the record before it acts. Another tab ending the session for idleness
    // or at the cap ends this one too (`handle_sibling_logout.ts`), so a check still out is called off.
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === IDLE_SIGN_OUT_STORAGE_KEY ||
        event.key === SESSION_CAP_SIGN_OUT_STORAGE_KEY
      ) {
        if (event.newValue === null) return;
        if (pending) pending.live = false;
        pending = { live: false };
      } else if (event.key === SESSION_DEADLINES_STORAGE_KEY) evaluate();
      else if (event.key === LAST_ACTIVITY_STORAGE_KEY && warningRef.current) evaluate();
    };

    // a laptop waking from sleep: its timers may not have caught up yet
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") evaluate();
    };

    // Opening a tab, or reloading one, is not input: the clock goes on from the shared record. Only a
    // tab with no record to read -- storage unavailable, or none written since signing in -- starts it.
    const recorded = readLastActivity();
    if (recorded) ownActivityAtRef.current = recorded.at;
    else noteActivity(Date.now());
    ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, onInput, true));
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibilityChange);
    evaluate();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, onInput, true));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timer) clearTimeout(timer);
      if (trailingWrite) clearTimeout(trailingWrite);
      if (pending) pending.live = false;
      reporter.stop();
      reporterRef.current = null;
      evaluateRef.current = null;
      heldRef.current = false;
      hideWarnings();
    };
  }, [signedIn, sessionNonce, noteActivity, endSession, dispatch, store]);

  // the server's deadlines moved in this tab: a refresh or an activity report answered
  useEffect(() => {
    evaluateRef.current?.();
  }, [sessionDeadlines]);

  // Renews the session only once the server has: a report that fails changes nothing here either, so
  // the warning stays up and its deadline stands. It is never taken for a check.
  const staySignedIn = useCallback(async () => {
    const reporter = reporterRef.current;
    if (signingOutRef.current || heldRef.current || !reporter) return;
    // a deadline that passed while the timer ran late goes to the server rather than being extended
    evaluateRef.current?.();
    if (signingOutRef.current || heldRef.current) return;
    setStayPending(true);
    const confirmed = await reporter.confirmPresence();
    setStayPending(false);
    if (!confirmed || signingOutRef.current || reporterRef.current !== reporter) return;
    noteActivity(Date.now());
    evaluateRef.current?.();
  }, [noteActivity]);

  const signOut = useCallback(() => {
    void endSession(null);
  }, [endSession]);

  return { signOutAt, capEndsAt, staySignedIn, signOut, stayPending };
};
